import React from 'react';

const mockRoadmapData = {
  currentFocus: {
    skill: 'System Design',
    nextMilestone: 'Design a distributed cache system',
    deadline: '2024-03-30'
  },
  actionItems: [
    {
      id: 1,
      title: 'Master Microservices Architecture',
      description: 'Focus on service discovery, load balancing, and fault tolerance',
      status: 'in-progress',
      deadline: '2024-04-15',
      resources: [
        { type: 'video', title: 'Microservices Design Patterns', link: '#' },
        { type: 'practice', title: 'Design Instagram Backend', link: '#' }
      ]
    },
    {
      id: 2,
      title: 'Improve Problem-Solving Speed',
      description: 'Practice medium-level algorithm questions with time constraints',
      status: 'pending',
      deadline: '2024-04-01',
      resources: [
        { type: 'practice', title: 'Two Pointers Technique', link: '#' },
        { type: 'document', title: 'Dynamic Programming Guide', link: '#' }
      ]
    },
    {
      id: 3,
      title: 'Behavioral Interview Preparation',
      description: 'Prepare STAR method responses for leadership scenarios',
      status: 'completed',
      deadline: '2024-03-20',
      resources: [
        { type: 'template', title: 'STAR Response Template', link: '#' },
        { type: 'video', title: 'Leadership Question Examples', link: '#' }
      ]
    }
  ],
  skillGaps: [
    { skill: 'Distributed Systems', current: 60, target: 85 },
    { skill: 'Algorithm Speed', current: 70, target: 90 },
    { skill: 'System Design', current: 75, target: 85 }
  ]
};

const StatusBadge = ({ status }) => {
  const statusStyles = {
    'completed': 'bg-green-100 text-green-800',
    'in-progress': 'bg-blue-100 text-blue-800',
    'pending': 'bg-yellow-100 text-yellow-800'
  };

  return (
    <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${statusStyles[status]}`}>
      {status.charAt(0).toUpperCase() + status.slice(1)}
    </span>
  );
};

const ResourceLink = ({ type, title }) => {
  const iconMap = {
    video: (
      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M14.752 11.168l-3.197-2.132A1 1 0 0010 9.87v4.263a1 1 0 001.555.832l3.197-2.132a1 1 0 000-1.664z"></path>
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M21 12a9 9 0 11-18 0 9 9 0 0118 0z"></path>
      </svg>
    ),
    practice: (
      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2"></path>
      </svg>
    ),
    document: (
      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M7 21h10a2 2 0 002-2V9.414a1 1 0 00-.293-.707l-5.414-5.414A1 1 0 0012.586 3H7a2 2 0 00-2 2v14a2 2 0 002 2z"></path>
      </svg>
    ),
    template: (
      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 5a1 1 0 011-1h14a1 1 0 011 1v2a1 1 0 01-1 1H5a1 1 0 01-1-1V5zM4 13a1 1 0 011-1h6a1 1 0 011 1v6a1 1 0 01-1 1H5a1 1 0 01-1-1v-6zM16 13a1 1 0 011-1h2a1 1 0 011 1v6a1 1 0 01-1 1h-2a1 1 0 01-1-1v-6z"></path>
      </svg>
    )
  };

  return (
    <a href="#" className="flex items-center text-sm text-blue-600 hover:text-blue-800">
      <span className="mr-2">{iconMap[type]}</span>
      {title}
    </a>
  );
};

const ImprovementRoadmap = () => {
  return (
    <div className="max-w-7xl mx-auto px-4 py-8">
      <h1 className="text-3xl font-bold text-gray-900 mb-8">Your Learning Roadmap</h1>

      {/* Current Focus */}
      <div className="bg-blue-50 border-l-4 border-blue-500 p-6 rounded-r-lg mb-8">
        <h2 className="text-xl font-semibold text-blue-900 mb-2">Current Focus</h2>
        <p className="text-blue-800 mb-1">Skill: {mockRoadmapData.currentFocus.skill}</p>
        <p className="text-blue-800 mb-1">Next Milestone: {mockRoadmapData.currentFocus.nextMilestone}</p>
        <p className="text-blue-600">Target Date: {mockRoadmapData.currentFocus.deadline}</p>
      </div>

      {/* Skill Gaps */}
      <div className="bg-white p-6 rounded-lg shadow-md mb-8">
        <h2 className="text-xl font-semibold text-gray-800 mb-6">Skill Gaps to Address</h2>
        <div className="space-y-4">
          {mockRoadmapData.skillGaps.map((gap, index) => (
            <div key={index} className="flex items-center">
              <div className="flex-1">
                <div className="flex justify-between mb-1">
                  <span className="text-sm font-medium text-gray-700">{gap.skill}</span>
                  <span className="text-sm font-medium text-gray-700">{gap.current}% → {gap.target}%</span>
                </div>
                <div className="w-full bg-gray-200 rounded-full h-2.5">
                  <div
                    className="bg-blue-600 h-2.5 rounded-full"
                    style={{ width: `${(gap.current / gap.target) * 100}%` }}
                  ></div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Action Items */}
      <div className="bg-white p-6 rounded-lg shadow-md">
        <h2 className="text-xl font-semibold text-gray-800 mb-6">Action Items</h2>
        <div className="space-y-6">
          {mockRoadmapData.actionItems.map((item) => (
            <div key={item.id} className="border-b border-gray-200 pb-6 last:border-0 last:pb-0">
              <div className="flex items-center justify-between mb-2">
                <h3 className="text-lg font-medium text-gray-900">{item.title}</h3>
                <StatusBadge status={item.status} />
              </div>
              <p className="text-gray-600 mb-3">{item.description}</p>
              <div className="flex items-center justify-between">
                <div className="space-y-2">
                  {item.resources.map((resource, index) => (
                    <ResourceLink key={index} type={resource.type} title={resource.title} />
                  ))}
                </div>
                <span className="text-sm text-gray-500">Due: {item.deadline}</span>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default ImprovementRoadmap; 